import { cn } from "tailwind-variants";
import { useI18n } from "intl/useI18n";
import { useDtfStore } from "../store";
import MultiSizeTotalSummary from "./MultiSizeTotalSummary.vue";
import "./MultiSizeTotal.scss";

export default function MultiSizeTotal({ index = 0, isMultiUpload = false, ...restProps }) {
  const { t, n } = useI18n();
  const store = useDtfStore();

  function artworks() {
    return store.artworks;
  }

  function summary() {
    return store.summary;
  }

  function simulation() {
    return store.simulation;
  }

  function isPricingLoading() {
    return store.isPricingLoading;
  }

  function enableAbAiMonetization() {
    return store.enableAbAiMonetization;
  }

  function getBogoAdjustedPrice() {
    return store.getBogoAdjustedPrice;
  }

  function artwork() {
    return artworks()[index];
  }

  function isArtworkUploaded() {
    const artworkId = parseInt(artwork()?.id, 10);
    if (Number.isNaN(artworkId)) {
      return false;
    }
    return typeof artworkId === "number" && !artwork()?.isLoading;
  }

  function showPricing() {
    return index === 0 || isArtworkUploaded();
  }

  function sizesAndQuantities() {
    return artwork()?.sizesAndQuantities || [];
  }

  function sizesCount() {
    return sizesAndQuantities().length;
  }

  function quantity() {
    return sizesAndQuantities().reduce((acc, curr) => acc + Number(curr.quantity), 0);
  }

  function pricing() {
    if (artwork()?.isFile || !sizesAndQuantities().some(sq => Number(sq.quantity) > 0)) {
      return simulation();
    }
    return sizesAndQuantities() || simulation();
  }

  function retailTotal() {
    return getPrice("retailPrice");
  }

  function total() {
    return getPrice("unitPrice");
  }

  function discount() {
    const retail = parseFloat(retailTotal());
    const actual = parseFloat(total());
    if (!retail || !actual) return 0;
    return Math.ceil(((retail - actual) / retail) * 100).toFixed(0);
  }

  function getPrice(propertyName) {
    if (Array.isArray(pricing())) {
      const artworkId = String(artwork()?.id ?? "");
      return pricing()
        .reduce((sum, item) => {
          if (propertyName === "unitPrice" && artworkId) {
            const sizeKey = `${item.width}x${item.height}`;
            const bogoPrice = getBogoAdjustedPrice()(artworkId, item.id, sizeKey);
            if (bogoPrice) {
              return sum + bogoPrice.discountedTotalPrice;
            }
          }
          const price = Number(item[propertyName] || 0);
          return sum + price * item.quantity;
        }, 0)
        .toFixed(2);
    }
    return pricing()[propertyName]
      ? Number(pricing()[propertyName]).toFixed(2) * pricing().quantity
      : null;
  }

  return (
    <>
      {!isMultiUpload && enableAbAiMonetization() ? (
        <MultiSizeTotalSummary
          {...restProps}
          index={index}
          data-testid="multi-size-total-summary"
        />
      ) : pricing() ? (
        <div
          {...restProps}
          className={cn("multi-size-total", restProps?.className, {
            "multi-size-total--multi-upload": isMultiUpload
          })}
        >
          <div className="multi-size-total__container">
            <div className="multi-size-total__label multi-size-total__label--total">Total</div>
            {sizesCount() > 0 ? (
              <div className="multi-size-total__label multi-size-total__label--sizes">
                {" "}
                for {t("transfer", quantity())}
              </div>
            ) : null}
          </div>
          {sizesCount() > 0 && total() && showPricing() ? (
            <div
              className={cn("multi-size-total__container multi-size-total__container--horizontal", {
                "multi-size-total__container--loading": isPricingLoading() || total() === 0
              })}
            >
              <div className="multi-size-total__label">{n(total(), "currency")}</div>
              {retailTotal() && discount() > 0 ? (
                <div className="multi-size-total__label multi-size-total__label--strike-of">
                  {n(retailTotal(), "currency")}
                </div>
              ) : null}
              {discount() > 0 ? (
                <div className="multi-size-total__label multi-size-total__label--discounted">
                  {discount()}% off{" "}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
